var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import './../card/ZoneCard.js';
let ZoneList = class ZoneList extends LitElement {
    constructor() {
        super(...arguments);
        this.zones = [];
        this.hiddenZoneIds = [];
        this.loading = false;
        this.draggable = true;
        // Drag-and-drop transient state. dragIndex/dragOverIndex refer to indices
        // in `sortedZones()` (i.e. the rendered order), not the original zones.
        this.dragIndex = null;
        this.dragOverIndex = null;
        this.dragOverPosition = null;
        this.onDragStart = (e) => {
            const idx = this.getCardIndex(e.target);
            if (idx == null)
                return;
            this.dragIndex = idx;
            if (e.dataTransfer) {
                e.dataTransfer.effectAllowed = 'move';
                // Some browsers require setData to initiate drag.
                e.dataTransfer.setData('text/plain', String(idx));
            }
        };
        this.onDragOver = (e) => {
            if (this.dragIndex == null)
                return;
            const targetIdx = this.getCardIndex(e.target);
            if (targetIdx == null || targetIdx === this.dragIndex) {
                this.dragOverIndex = null;
                this.dragOverPosition = null;
                return;
            }
            e.preventDefault();
            if (e.dataTransfer)
                e.dataTransfer.dropEffect = 'move';
            // Decide whether to insert before or after based on cursor position
            // relative to the card's vertical midpoint.
            const card = e.target.closest('zone-card');
            const rect = card.getBoundingClientRect();
            const midY = rect.top + rect.height / 2;
            this.dragOverPosition = e.clientY < midY ? 'before' : 'after';
            this.dragOverIndex = targetIdx;
        };
        this.onDrop = (e) => {
            if (this.dragIndex == null || this.dragOverIndex == null) {
                this.resetDragState();
                return;
            }
            e.preventDefault();
            const sorted = this.sortedZones();
            const [moved] = sorted.splice(this.dragIndex, 1);
            if (!moved) {
                this.resetDragState();
                return;
            }
            let insertAt = this.dragOverPosition === 'after'
                ? this.dragOverIndex + 1
                : this.dragOverIndex;
            // After splice the indices shifted: if we removed before insertAt, decrement.
            if (this.dragIndex < insertAt)
                insertAt -= 1;
            sorted.splice(insertAt, 0, moved);
            // Re-assign priorities 1-indexed: top card = priority 1, bottom = n.
            const reordered = sorted.map((z, i) => ({
                ...z,
                priority: i + 1,
            }));
            this.dispatchEvent(new CustomEvent('zones-reorder', {
                detail: { zones: reordered },
                bubbles: true,
                composed: true,
            }));
            this.resetDragState();
        };
        this.onDragEnd = () => {
            this.resetDragState();
        };
    }
    // ───────────── helpers ─────────────
    sortedZones() {
        // Ascending by priority: priority 1 first, then 2, 3, …
        // Position in the list IS the hierarchy — there is no priority badge.
        return [...this.zones].sort((a, b) => {
            const pa = a.priority ?? 0;
            const pb = b.priority ?? 0;
            if (pa !== pb)
                return pa - pb;
            // Stable secondary sort on zone_id to keep order deterministic.
            return a.zone_id - b.zone_id;
        });
    }
    handleZoneClick(zone) {
        this.selectedZone = zone;
        this.dispatchEvent(new CustomEvent('zone-click', {
            detail: zone,
            bubbles: true,
            composed: true,
        }));
    }
    // ───────────── drag-and-drop ─────────────
    getCardIndex(target) {
        if (!(target instanceof HTMLElement))
            return null;
        const card = target.closest('zone-card');
        if (!card)
            return null;
        const idxAttr = card.dataset.idx;
        return idxAttr != null ? Number(idxAttr) : null;
    }
    resetDragState() {
        this.dragIndex = null;
        this.dragOverIndex = null;
        this.dragOverPosition = null;
    }
    // ───────────── render ─────────────
    renderEmpty() {
        return html `
      <div class="zone-list__empty">
        <div class="empty-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 6l9 4 9-4-9-4-9 4z"></path>
            <path d="M3 6v12l9 4 9-4V6"></path>
          </svg>
        </div>
        <div class="empty-title">${msg('No zones', { id: 'zoneList.empty.title', desc: 'Title shown when the zone list is empty' })}</div>
        <div class="empty-text">
          ${this.emptyLabel ?? msg('No zones to display.', { id: 'zoneList.empty.text', desc: 'Message shown when the zone list is empty' })}
        </div>
      </div>
    `;
    }
    renderLoading() {
        return html `
      <div class="zone-list__loading">
        <div class="spinner"></div>
        <div class="empty-text">${this.emptyLabel ?? msg('Loading zones…', { id: 'zoneList.loading', desc: 'Message shown while zones are loading' })}</div>
      </div>
    `;
    }
    render() {
        const sorted = this.sortedZones();
        const hasZones = sorted.length > 0;
        return html `
      <div class="zone-list">
        ${this.listTitle
            ? html `
              <div class="zone-list__header">
                <h3 class="zone-list__title">${this.listTitle}</h3>
                <span class="zone-list__count"
                  >${hasZones ? sorted.length : 0}</span
                >
              </div>
            `
            : null}
        ${this.loading
            ? this.renderLoading()
            : hasZones
                ? html `
                <div
                  class="zone-list__items"
                  @dragstart=${this.draggable ? this.onDragStart : null}
                  @dragover=${this.draggable ? this.onDragOver : null}
                  @drop=${this.draggable ? this.onDrop : null}
                  @dragend=${this.draggable ? this.onDragEnd : null}
                >
                  ${sorted.map((zone, idx) => {
                    const isDragging = this.dragIndex === idx;
                    const isDropTarget = this.dragOverIndex === idx;
                    const dropBefore = isDropTarget && this.dragOverPosition === 'before';
                    const dropAfter = isDropTarget && this.dragOverPosition === 'after';
                    const isHidden = this.hiddenZoneIds.includes(zone.zone_id);
                    return html `
                      <zone-card
                        data-idx=${idx}
                        draggable=${this.draggable ? 'true' : 'false'}
                        ?dragging=${isDragging}
                        ?drop-before=${dropBefore}
                        ?drop-after=${dropAfter}
                        .zone=${zone}
                        .zoneTypeName=${this.zoneTypeNames?.[zone.zone_type_id] ?? ''}
                        .geolevelName=${zone.geolevel_id != null
                        ? (this.geolevelNames?.[zone.geolevel_id] ?? '')
                        : ''}
                        ?selected=${this.selectedZone?.zone_id === zone.zone_id}
                        ?hidden=${isHidden}
                        @zone-click=${() => this.handleZoneClick(zone)}
                      ></zone-card>
                    `;
                })}
                </div>
              `
                : this.renderEmpty()}
      </div>
    `;
    }
};
ZoneList.styles = css `
    :host {
      display: block;
      background: #fff;
      font-family: inherit;
      color: #333;
    }

    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    .zone-list__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 16px;
      border-bottom: 1px solid #eee;
    }

    .zone-list__title {
      margin: 0;
      font-size: 13px;
      font-weight: 600;
      color: #333;
      letter-spacing: 0.02em;
    }

    .zone-list__count {
      font-size: 11px;
      color: #666;
      background: #f0f4f8;
      padding: 2px 8px;
      border-radius: 10px;
      font-weight: 500;
    }

    .zone-list__items zone-card {
      display: block;
    }

    .zone-list__items zone-card + zone-card {
      border-top: 1px solid #f0f0f0;
    }

    .zone-list__empty,
    .zone-list__loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 32px 24px;
      text-align: center;
      color: #888;
      font-size: 12px;
    }

    .empty-icon {
      width: 48px;
      height: 48px;
      background: #f0f0f0;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 10px;
      color: #bbb;
    }

    .empty-icon svg {
      width: 22px;
      height: 22px;
    }

    .empty-title {
      font-size: 13px;
      font-weight: 600;
      color: #555;
      margin-bottom: 4px;
    }

    .empty-text {
      font-size: 11px;
      color: #999;
      max-width: 260px;
    }

    .spinner {
      width: 22px;
      height: 22px;
      border: 2px solid #e0e0e0;
      border-top-color: #0078d4;
      border-radius: 50%;
      animation: zl-spin 0.8s linear infinite;
      margin-bottom: 8px;
    }

    @keyframes zl-spin {
      to {
        transform: rotate(360deg);
      }
    }
  `;
__decorate([
    property({ type: Array })
], ZoneList.prototype, "zones", void 0);
__decorate([
    property({ type: Object })
], ZoneList.prototype, "zoneTypeNames", void 0);
__decorate([
    property({ type: Object })
], ZoneList.prototype, "geolevelNames", void 0);
__decorate([
    property({ type: Array })
], ZoneList.prototype, "hiddenZoneIds", void 0);
__decorate([
    property({ type: String })
], ZoneList.prototype, "listTitle", void 0);
__decorate([
    property({ type: String })
], ZoneList.prototype, "emptyLabel", void 0);
__decorate([
    property({ type: Boolean })
], ZoneList.prototype, "loading", void 0);
__decorate([
    property({ type: Boolean, attribute: 'draggable-zones' })
], ZoneList.prototype, "draggable", void 0);
__decorate([
    property({ type: Object })
], ZoneList.prototype, "selectedZone", void 0);
__decorate([
    state()
], ZoneList.prototype, "dragIndex", void 0);
__decorate([
    state()
], ZoneList.prototype, "dragOverIndex", void 0);
__decorate([
    state()
], ZoneList.prototype, "dragOverPosition", void 0);
ZoneList = __decorate([
    localized(),
    customElement('zone-list')
], ZoneList);
export { ZoneList };
//# sourceMappingURL=ZoneList.js.map